<template>
    <DCard
        padding
        :title="$t('settings_certificates.issue_mode_title')"
    >
        <DRadioGroup
            id="duplicateHandling"
            v-model="form.issue_mode"
            inline
            :options="[
                { value: 'rundeck', label: $t('settings_certificates.issue_mode_rundeck')},
                { value: 'internal', label: $t('settings_certificates.issue_mode_internal')},
            ]"
        />
    </DCard>
    <DCard
        padding
        :title="$t('settings_certificates.default_subject_title')"
        class="mt-3"
    >
        <DCol>
            <DFormRow
                :label="$t('settings_certificates.organization')"
                is-required
                :error="errors.organization"
            >
                <DInput v-model="form.organization" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.department')"
                is-required
                :error="errors.department"
            >
                <DInput v-model="form.department" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.country')"
                is-required
                :error="errors.country"
            >
                <DInput v-model="form.country" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.state')"
                is-required
                :error="errors.state"
            >
                <DInput v-model="form.state" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.location')"
                is-required
                :error="errors.location"
            >
                <DInput v-model="form.location" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.email')"
                is-required
                :error="errors.email"
            >
                <DInput v-model="form.email" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.phone')"
                :error="errors.phone"
            >
                <DInput v-model="form.phone" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.address')"
                is-required
                :error="errors.address"
            >
                <DInput v-model="form.address" />
            </DFormRow>
            <DFormRow
                :label="$t('settings_certificates.postal_code')"
                is-required
                :error="errors.postal_code"
            >
                <DInput v-model="form.postal_code" />
            </DFormRow>
        </DCol>
    </DCard>

    <DCard
        padding
        :title="$t('settings_certificates.supported_types_title')"
        class="mt-3"
    >
        <section
            v-for="provider in providers"
            :key="provider.id"
            class="provider-section"
        >
            <header class="provider-header">
                <h6 class="provider-title">
                    {{ provider.label }}
                </h6>
                <p class="provider-description">
                    {{ provider.description }}
                </p>
            </header>

            <div
                v-for="type in provider.types"
                :key="type.key"
                class="cert-type-block"
            >
                <div class="cert-type-toggle">
                    <DFormCheck
                        :id="`enable-${type.key}`"
                        v-model="form[type.key]"
                    />
                    <label
                        :for="`enable-${type.key}`"
                        class="cert-type-label ms-3"
                    >{{ type.label }}</label>
                </div>

                <div
                    class="variant-grid"
                    :class="{ 'is-inactive': !form[type.key] }"
                >
                    <div class="variant-col">
                        <p
                            v-if="type.supportsMulti"
                            class="variant-heading"
                        >
                            {{ $t('settings_certificates.variant_single_domain') }}
                        </p>
                        <DFormRow
                            :label="$t('settings_certificates.product_id')"
                            :error="errors[`${type.key}_product_id`]"
                        >
                            <DInput
                                v-model="form[`${type.key}_product_id`]"
                                :disabled="!form[type.key]"
                                :placeholder="$t('settings_certificates.placeholder_external_product_id')"
                                :feedback-invalid="errors[`${type.key}_product_id`]"
                            />
                        </DFormRow>
                        <DFormRow
                            :label="$t('settings_certificates.validity_days')"
                            :error="errors[`${type.key}_validity`]"
                        >
                            <DInput
                                v-model="form[`${type.key}_validity`]"
                                :disabled="!form[type.key]"
                                :placeholder="$t('settings_certificates.placeholder_number_of_days')"
                                :feedback-invalid="errors[`${type.key}_validity`]"
                            />
                        </DFormRow>
                    </div>

                    <div
                        v-if="type.supportsMulti"
                        class="variant-col"
                    >
                        <p class="variant-heading">
                            {{ $t('settings_certificates.variant_multi_domain') }}
                        </p>
                        <DFormRow
                            :label="$t('settings_certificates.product_id')"
                            :error="errors[`${type.key}_multi_product_id`]"
                        >
                            <DInput
                                v-model="form[`${type.key}_multi_product_id`]"
                                :disabled="!form[type.key]"
                                :placeholder="$t('settings_certificates.placeholder_external_product_id')"
                                :feedback-invalid="errors[`${type.key}_multi_product_id`]"
                            />
                        </DFormRow>
                        <DFormRow
                            :label="$t('settings_certificates.validity_days')"
                            :error="errors[`${type.key}_multi_validity`]"
                        >
                            <DInput
                                v-model="form[`${type.key}_multi_validity`]"
                                :disabled="!form[type.key]"
                                :placeholder="$t('settings_certificates.placeholder_number_of_days')"
                                :feedback-invalid="errors[`${type.key}_multi_validity`]"
                            />
                        </DFormRow>
                        <p class="variant-help">
                            {{ $t('settings_certificates.variant_multi_domain_help') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </DCard>
    <DFabs>
        <DFab
            icon="content-save"
            :label="$t('common.save')"
            @click="submitForm"
        />
    </DFabs>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import { computed, getCurrentInstance } from 'vue'

const { proxy } = getCurrentInstance()

const props = defineProps({
    ownerGroup: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const providers = computed(() => [
    {
        id: 'le',
        label: proxy.$t('settings_certificates.provider_le_label'),
        description: proxy.$t('settings_certificates.provider_le_description'),
        types: [
            { key: 'le_dv', label: proxy.$t('settings_certificates.cert_type_dv'), supportsMulti: false }
        ]
    },
    {
        id: 'sectigo',
        label: proxy.$t('settings_certificates.provider_sectigo_label'),
        description: proxy.$t('settings_certificates.provider_sectigo_description'),
        types: [
            { key: 'sectigo_dv', label: proxy.$t('settings_certificates.cert_type_dv'), supportsMulti: true },
            { key: 'sectigo_ov', label: proxy.$t('settings_certificates.cert_type_ov'), supportsMulti: true },
            { key: 'sectigo_ev', label: proxy.$t('settings_certificates.cert_type_ev'), supportsMulti: true },
            { key: 'sectigo_dv_wildcard', label: proxy.$t('settings_certificates.cert_type_dv_wildcard'), supportsMulti: false },
            { key: 'sectigo_ov_wildcard', label: proxy.$t('settings_certificates.cert_type_ov_wildcard'), supportsMulti: false }
        ]
    },
    {
        id: 'greenle',
        label: proxy.$t('settings_certificates.provider_greenle_label'),
        description: proxy.$t('settings_certificates.provider_greenle_description'),
        types: [
            { key: 'greenle_dv', label: proxy.$t('settings_certificates.cert_type_dv'), supportsMulti: false }
        ]
    }
])

const form = useForm({
    owner_group_id: props.ownerGroup.id ?? null,
    organization: props.ownerGroup.organization ?? null,
    department: props.ownerGroup.department ?? null,
    country: props.ownerGroup.country ?? null,
    state: props.ownerGroup.state ?? null,
    location: props.ownerGroup.location ?? null,
    email: props.ownerGroup.email ?? null,
    phone: props.ownerGroup.phone ?? null,
    address: props.ownerGroup.address ?? null,
    postal_code: props.ownerGroup.postal_code ?? null,
    le_dv: props.ownerGroup.le_dv ?? false,
    sectigo_dv: props.ownerGroup.sectigo_dv ?? false,
    sectigo_ov: props.ownerGroup.sectigo_ov ?? false,
    sectigo_ev: props.ownerGroup.sectigo_ev ?? false,
    sectigo_dv_wildcard: props.ownerGroup.sectigo_dv_wildcard ?? false,
    sectigo_ov_wildcard: props.ownerGroup.sectigo_ov_wildcard ?? false,
    greenle_dv: props.ownerGroup.greenle_dv ?? false,
    le_dv_product_id: props.ownerGroup.le_dv_product_id ?? '',
    sectigo_dv_product_id: props.ownerGroup.sectigo_dv_product_id ?? '',
    sectigo_dv_multi_product_id: props.ownerGroup.sectigo_dv_multi_product_id ?? '',
    sectigo_ov_product_id: props.ownerGroup.sectigo_ov_product_id ?? '',
    sectigo_ov_multi_product_id: props.ownerGroup.sectigo_ov_multi_product_id ?? '',
    sectigo_dv_wildcard_product_id: props.ownerGroup.sectigo_dv_wildcard_product_id ?? '',
    sectigo_ov_wildcard_product_id: props.ownerGroup.sectigo_ov_wildcard_product_id ?? '',
    sectigo_ev_product_id: props.ownerGroup.sectigo_ev_product_id ?? '',
    sectigo_ev_multi_product_id: props.ownerGroup.sectigo_ev_multi_product_id ?? '',
    greenle_dv_product_id: props.ownerGroup.greenle_dv_product_id ?? '',
    le_dv_validity: props.ownerGroup.le_dv_validity ?? '',
    sectigo_dv_validity: props.ownerGroup.sectigo_dv_validity ?? '',
    sectigo_dv_multi_validity: props.ownerGroup.sectigo_dv_multi_validity ?? '',
    sectigo_ov_validity: props.ownerGroup.sectigo_ov_validity ?? '',
    sectigo_ov_multi_validity: props.ownerGroup.sectigo_ov_multi_validity ?? '',
    sectigo_dv_wildcard_validity: props.ownerGroup.sectigo_dv_wildcard_validity ?? '',
    sectigo_ov_wildcard_validity: props.ownerGroup.sectigo_ov_wildcard_validity ?? '',
    sectigo_ev_validity: props.ownerGroup.sectigo_ev_validity ?? '',
    sectigo_ev_multi_validity: props.ownerGroup.sectigo_ev_multi_validity ?? '',
    greenle_dv_validity: props.ownerGroup.greenle_dv_validity ?? '',
    issue_mode: props.ownerGroup.issue_mode ?? false
})

const submitForm = () => {
    form.post(route('settings.certificate.store'))
}
</script>

<style scoped>
.provider-section {
    margin-bottom: 2rem;
}
.provider-section + .provider-section {
    border-top: 1px solid var(--bs-border-color, #dee2e6);
    padding-top: 1.5rem;
}
.provider-header {
    margin-bottom: 1rem;
}
.provider-title {
    font-weight: 600;
    margin-bottom: 0.25rem;
}
.provider-description {
    color: var(--bs-secondary-color, #6c757d);
    font-size: 0.875rem;
    margin-bottom: 0;
}
.cert-type-block {
    border-left: 3px solid var(--bs-border-color, #dee2e6);
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
}
.cert-type-toggle {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}
.cert-type-label {
    font-weight: 500;
    cursor: pointer;
}
.variant-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    transition: opacity 0.15s ease-in-out;
}
@media (min-width: 992px) {
    .variant-grid {
        grid-template-columns: 1fr 1fr;
    }
}
.variant-grid.is-inactive {
    opacity: 0.55;
}
.variant-heading {
    font-weight: 600;
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color, #6c757d);
    margin-bottom: 0.5rem;
}
.variant-help {
    color: var(--bs-secondary-color, #6c757d);
    font-size: 0.8125rem;
    margin-top: 0.25rem;
    margin-bottom: 0;
}
</style>
